/* eslint-disable react-refresh/only-export-components -- Router config file legitimately mixes component definitions with router exports */
import { Suspense, lazy } from "react";
import {
  createRootRoute,
  createRoute,
  createRouter,
  ErrorComponentProps,
  Outlet,
} from "@tanstack/react-router";
import { useAuth } from "./contexts/AuthContext";
import { LandingPage } from "./components";
import SubscribedApp from "./components/billing/SubscribedApp";
import GoogleCalendarCallback from "./pages/GoogleCalendarCallback";
import ConnectAgentPage from "./pages/ConnectAgentPage";
import NotFoundPage from "./components/misc/NotFoundPage";
import ErrorFallback from "./components/misc/ErrorFallback";
import { withChunkReloadGuard } from "./lib/chunkReloadGuard";
import { searchParamString } from "./lib/searchParamString";

// The Help & Guide page is a standalone reference route that most sessions
// never open, so it loads on demand rather than riding along with the app.
const DocsPage = lazy(
  withChunkReloadGuard("docsChunkReloaded", () => import("./pages/DocsPage")),
);

const LegalPage = lazy(
  withChunkReloadGuard("legalChunkReloaded", () => import("./pages/LegalPage")),
);

// Search param validation shared by both routes
const searchSchema = {
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    filter?: string;
    priorities?: string;
    lists?: string;
    tags?: string;
    subtask?: string;
    account?: string;
    sender?: string;
    thread?: string;
  } => ({
    filter: typeof search.filter === "string" ? search.filter : undefined,
    priorities: searchParamString(search.priorities),
    lists:
      typeof search.lists === "string" ? search.lists : undefined,
    tags: typeof search.tags === "string" ? search.tags : undefined,
    subtask: typeof search.subtask === "string" ? search.subtask : undefined,
    // The Emails view's open account/sender/conversation — see
    // useEmailPageUrlSync, which reads these back out to restore the same
    // email in the same mailbox after a refresh.
    account: typeof search.account === "string" ? search.account : undefined,
    sender: typeof search.sender === "string" ? search.sender : undefined,
    thread: typeof search.thread === "string" ? search.thread : undefined,
  }),
};

const RootLayout = () => <Outlet />;

const AuthedLayout = () => {
  const { user, isLoading } = useAuth();

  if (isLoading && !user) {
    return (
      <div className="min-h-screen bg-warm-bg-base flex items-center justify-center">
        <div className="w-8 h-8 border-[3px] border-warm-border border-t-warm-blue rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <LandingPage />;
  }

  return <Outlet />;
};

const RouteErrorComponent = (_props: ErrorComponentProps) => {
  return <ErrorFallback />;
};

const PublicPageFallback = ({ label }: { label: string }) => (
  <div className="min-h-screen bg-warm-bg-base flex items-center justify-center">
    <div
      role="status"
      aria-label={label}
      className="w-8 h-8 border-[3px] border-warm-border border-t-warm-blue rounded-full animate-spin"
    />
  </div>
);

const rootRoute = createRootRoute({
  component: RootLayout,
  notFoundComponent: NotFoundPage,
  errorComponent: RouteErrorComponent,
});

const authedLayout = createRoute({
  id: "authed",
  getParentRoute: () => rootRoute,
  component: AuthedLayout,
  errorComponent: RouteErrorComponent,
});

const indexRoute = createRoute({
  getParentRoute: () => authedLayout,
  path: "/",
  ...searchSchema,
  component: SubscribedApp,
  errorComponent: RouteErrorComponent,
});

const taskRoute = createRoute({
  getParentRoute: () => authedLayout,
  path: "/task/$taskId",
  ...searchSchema,
  component: SubscribedApp,
  errorComponent: RouteErrorComponent,
});

const calendarRoute = createRoute({
  getParentRoute: () => authedLayout,
  path: "/calendar",
  ...searchSchema,
  component: SubscribedApp,
  errorComponent: RouteErrorComponent,
});

const emailsRoute = createRoute({
  getParentRoute: () => authedLayout,
  path: "/emails",
  ...searchSchema,
  component: SubscribedApp,
  errorComponent: RouteErrorComponent,
});

const capturesRoute = createRoute({
  getParentRoute: () => authedLayout,
  path: "/captures",
  ...searchSchema,
  component: SubscribedApp,
  errorComponent: RouteErrorComponent,
});

const googleCalendarCallbackRoute = createRoute({
  getParentRoute: () => authedLayout,
  path: "/integrations/google-calendar/callback",
  component: GoogleCalendarCallback,
  errorComponent: RouteErrorComponent,
});

const connectAgentRoute = createRoute({
  getParentRoute: () => authedLayout,
  path: "/connect",
  component: ConnectAgentPage,
  errorComponent: RouteErrorComponent,
});

const docsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/docs",
  component: () => (
    <Suspense fallback={<PublicPageFallback label="Loading Help & Guide" />}>
      <DocsPage />
    </Suspense>
  ),
  errorComponent: RouteErrorComponent,
});

const termsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/terms",
  component: () => (
    <Suspense
      fallback={<PublicPageFallback label="Loading Terms of Service" />}
    >
      <LegalPage kind="terms" />
    </Suspense>
  ),
  errorComponent: RouteErrorComponent,
});

const privacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/privacy",
  component: () => (
    <Suspense
      fallback={<PublicPageFallback label="Loading Privacy Policy" />}
    >
      <LegalPage kind="privacy" />
    </Suspense>
  ),
  errorComponent: RouteErrorComponent,
});

const accountDeletionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/account-deletion",
  component: () => (
    <Suspense
      fallback={<PublicPageFallback label="Loading account deletion" />}
    >
      <LegalPage kind="account-deletion" />
    </Suspense>
  ),
  errorComponent: RouteErrorComponent,
});

const routeTree = rootRoute.addChildren([
  authedLayout.addChildren([
    indexRoute,
    taskRoute,
    calendarRoute,
    emailsRoute,
    capturesRoute,
    googleCalendarCallbackRoute,
    connectAgentRoute,
  ]),
  docsRoute,
  termsRoute,
  privacyRoute,
  accountDeletionRoute,
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
